import React, { useMemo, useRef } from "react";
import type { MutableRefObject, FC } from "react";
import { useStore } from "@nanostores/react";
import { useCarousel } from "./useCarousel";
import "@glidejs/glide/dist/css/glide.core.min.css";
import style from "./Carousel.module.css";
import type { CarouselProps, Subcomponents } from "./types";
import { Slide } from "./components/Slide";
import { Thumbnails } from "./components/Thumbnails";
import { Affix } from "./components/Affix";
import { Header } from "./components/Header";
import { useCarouselComponents } from "./useCarouselComponents";
import { $currentIndex } from "./Carousel.store";
import { useGlide } from "./useGlide";

const { wrapper, root, dot, dotsNavigation } = style;

export const Carousel: FC<CarouselProps> & Subcomponents = (props) => {
  const ref = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement>;

  // TODO remove "width" prop as it will be not required in the carousel V3
  const {
    children,
    displayAffix,
    dotsNav,
    width,
    id,
    style,
    title,
    type,
    perView,
    gap,
    peek,
    breakpoints,
    updateAction,
    onEvent,
  } = props;

  const childrensArray = React.Children.toArray(children);
  const currentIndex = useStore($currentIndex);

  const { glideInstance } = useGlide({
    ref,
    id,
    perView,
    gap,
    peek,
    type,
    breakpoints,
  });

  // We declare this outside the hook as totalSlides
  // could change and cause extra renders when passed down
  // to useCarousel.
  const slides = useMemo(
    () =>
      childrensArray.filter(
        (el) => React.isValidElement(el) && el.type === Slide
      ),
    [childrensArray]
  );

  const totalSlides = slides.length;

  const { updateSlide, handleAffixAction, lockedNext, lockedPrev } =
    useCarousel({
      type,
      updateAction,
      onEvent,
      glideInstance,
      totalSlides,
      id,
    });

  const { header, thumbnails, affix } = useCarouselComponents(
    childrensArray,
    handleAffixAction,
    updateSlide
  );

  const [previousButton, nextButton] = affix || [];

  if (!slides.length) return null;

  return (
    <>
      {header && (
        <Header title={title} lockedNext={lockedNext} lockedPrev={lockedPrev}>
          {displayAffix && nextButton}
          {displayAffix && previousButton}
        </Header>
      )}
      <div className={wrapper} style={style}>
        {thumbnails}

        <div
          className={`${root}`}
          id={id}
          ref={ref}
          tabIndex={0} // Make the carousel focusable so it can be navigated with keyboard
          style={{
            maxWidth: width ? `${width}px` : "auto",
          }}
        >
          {!header && displayAffix && previousButton}

          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">{slides}</ul>
          </div>

          {!header && displayAffix && nextButton}

          {dotsNav && (
            <nav
              data-current-index={currentIndex}
              aria-label="Carousel secondary navigation"
              className={dotsNavigation}
            >
              {[...Array(slides.length)].map((_, index) => (
                <button
                  key={index}
                  className={dot}
                  data-selected={index === currentIndex}
                  onClick={() => updateSlide(index)}
                />
              ))}
            </nav>
          )}
        </div>
      </div>
    </>
  );
};

Carousel.displayName = "Carousel";

Carousel.Slide = Slide;
Carousel.Thumbnails = Thumbnails;
Carousel.Affix = Affix || (() => null);
Carousel.Header = Header;
